/**
 * Frozen demo data shared by all three CardDetailHero slices.
 *
 * Field names, value shapes, and enum spellings mirror the approved Card
 * Management MVP API contract, so wiring real data becomes a mapping change in
 * `useCardDetailHeroData` rather than a rename sweep through every region.
 *
 * The grouping is deliberate: `card`, `limits`, and `billing` correspond to
 * three *independently* fetched endpoints. Regions read only the group they
 * need, so a later limits failure cannot blank the identity row.
 *
 * Money is always minor units plus an ISO-4217 currency, never a float —
 * conversion to a display amount happens at the formatter call site.
 */

/** Contract enum. LOCKED is issuer-imposed and is NOT a voluntary FROZEN. */
export type PhysicalCardManagementStatus =
  | 'PENDING_ISSUANCE'
  | 'ACTIVE'
  | 'FROZEN'
  | 'LOCKED'
  | 'CANCELED'
  | 'FAILED'

export type Money = {
  amountMinor: number
  currency: string
}

export const CARD_DETAIL_HERO_DEMO = {
  /** Mirrors GET /physical-cards/{id} — identity only, no feature aggregate. */
  card: {
    nickname: 'T&E Card',
    cardholder: {
      displayName: 'Jordan Avery',
    },
    last4: '5678',
    network: 'MASTERCARD',
    status: 'ACTIVE' as PhysicalCardManagementStatus,
  },

  /** Mirrors GET /physical-cards/{id}/limits — separate groups, never flattened. */
  limits: {
    permanentCardSpendControl: {
      limit: { amountMinor: 5000000, currency: 'USD' } as Money,
    },
    cardFinancials: {
      balance: { amountMinor: 770000, currency: 'USD' } as Money,
      availableSpend: { amountMinor: 4230000, currency: 'USD' } as Money,
    },
  },

  /** Mirrors GET /physical-cards/{id}/billing-address — scope CARD_ACCOUNT. */
  billing: {
    address: {
      line1: '120 Broadway',
      line2: 'Suite 400',
      locality: 'New York',
      region: 'NY',
      postalCode: '10001',
      countryCode: 'US',
    },
  },

  /**
   * Slice 2 reveal values. These have NO contract counterpart by design — the
   * public API never returns PAN or CVC, and a real reveal would come from a
   * separate vault path, not from any endpoint modeled above. They are
   * non-Luhn-valid synthetic strings kept solely so the slice-2 reveal UI has
   * something to render behind a fail-closed flag.
   */
  credentials: {
    pan: '5412 0000 0000 5678',
    exp: '12/28',
    cvc: '567',
  },
} as const
